import React, { memo, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

import DirectionsWalkIcon from "../../icons/DirectionsWalkIcon";
import ECGHeart from "../../icons/ECGHeart";
import LungIcon from "../../icons/LungIcon";
import StackedLineChartIcon from "../../icons/StackedLineChartIcon";
import ResultCard from "../../common/ResultCard";
import ResultViewScaffold from "../ResultViewScaffold";
import { MultiMetricSummarySection } from "../PostExertionRespiratorySummary";
import ResultPendingDots from "../ResultPendingDots";
import RecordingQualitySection from "../RecordingQualitySection";
import { ResultSummaryRow } from "../ResultSummarySection";
import { useAuth } from "../../../context/AuthContext";
import { useMeasurementResultDoc } from "../../../hooks/results/useMeasurementResultDoc";
import {
  formatRecordingIdDateParts,
  parseTimestampFromRecordingId,
} from "../../../utils/results/recordingIdTime";
import {
  getUploadFailedUserMessage,
  isUploadFailedDoc,
} from "../../../utils/results/isUploadFailedDoc";
import {
  getResultModeIcon,
  getResultModeLabel,
} from "../../../utils/results/resultModePresentation";
import { pickHeartDxHrBpm } from "../../../utils/results/heartdx";
import { pickHrSignalQuality, pickRr } from "../../../utils/results/signalQuality";
import { isWithinPendingWindow } from "../../../utils/results/metricPending";
import {
  compareSixMinuteWalkInspiratoryReserve,
  pickSixMinuteWalkDetrendedSignal,
  pickSixMinuteWalkDistanceMeters,
  pickSixMinuteWalkGeoRouteTrace,
  pickSixMinuteWalkPairedMeasurementId,
  pickSixMinuteWalkRecoveryDelayMs,
  pickSixMinuteWalkRespiratoryQuality,
  pickSixMinuteWalkRespiratoryRate,
  pickSixMinuteWalkRouteTrace,
  pickSixMinuteWalkStage,
  pickSixMinuteWalkTotalTrackedDistanceMeters,
} from "../../../utils/results/sixMinuteWalk";
import {
  buildRecordingQualityCheck,
  getRecordingQualitySummaryCopy,
  RECORDING_QUALITY_OVERALL_STATES,
  summarizeRecordingQualityChecks,
} from "../../../utils/results/recordingQuality";
import SixMinuteWalkRouteMap from "../../charts/SixMinuteWalkRouteMap";
import SixMinuteWalkSignalChart from "../../charts/SixMinuteWalkSignalChart";

const RESULT_DEBUG_ENABLED = process.env.NODE_ENV === "development";

function logSixMinuteWalkResultDebug(level, message, details = undefined) {
  if (!RESULT_DEBUG_ENABLED || typeof console === "undefined") {
    return;
  }

  const logger =
    level === "error"
      ? console.error
      : level === "warn"
        ? console.warn
        : console.info;

  if (details === undefined) {
    logger(`[SixMinuteWalkResult] ${message}`);
    return;
  }

  logger(`[SixMinuteWalkResult] ${message}`, details);
}

function formatNumberValue(
  value,
  { maximumFractionDigits = 1, signDisplay = "auto" } = {},
) {
  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits,
    signDisplay,
  }).format(value);
}

function buildPendingMetricDisplay(
  value,
  formatter,
  unit = null,
  { recordingTimestamp = null, forcePending = false } = {},
) {
  if (typeof value === "number" && Number.isFinite(value)) {
    return {
      value: formatter(value),
      unit,
    };
  }

  if (forcePending || isWithinPendingWindow(recordingTimestamp)) {
    return {
      value: <ResultPendingDots className="muted-text" />,
      unit: null,
    };
  }

  return {
    value: "--",
    unit: null,
  };
}

function formatMetersValue(value) {
  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: value >= 1000 ? 0 : 1,
  }).format(value);
}

function formatSecondsFromMsValue(value) {
  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 0,
  }).format(value / 1000);
}

function formatFoldValue(value) {
  return `${formatNumberValue(value, { maximumFractionDigits: 1 })}x`;
}

function formatPercentValue(value) {
  return `${formatNumberValue(value, {
    maximumFractionDigits: 1,
    signDisplay: "always",
  })}%`;
}

function buildSignalDatasets({ preSignal, postSignal, t }) {
  return [
    {
      key: "pre",
      label: t("result.sixMinuteWalk.preSignalTitle", "Pre-walk"),
      points: preSignal.points,
    },
    {
      key: "post",
      label: t("result.sixMinuteWalk.postSignalTitle", "Post-walk"),
      points: postSignal.points,
    },
  ];
}

function SixMinuteWalkResultViewImpl({
  data,
  docId,
  variant,
  repeatLinkTo = "",
  footer = null,
}) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const lastRouteSummaryLogRef = useRef("");
  const stage = useMemo(() => pickSixMinuteWalkStage(data), [data]);
  const pairedMeasurementId = useMemo(
    () => pickSixMinuteWalkPairedMeasurementId(data),
    [data],
  );
  const preMeasurement = useMeasurementResultDoc(
    user?.uid,
    pairedMeasurementId ? variant : null,
    pairedMeasurementId,
  );

  const preData = preMeasurement.data;
  const preSignal = useMemo(
    () => pickSixMinuteWalkDetrendedSignal(preData),
    [preData],
  );
  const postSignal = useMemo(
    () => pickSixMinuteWalkDetrendedSignal(data),
    [data],
  );
  const respiratoryRatePre = useMemo(
    () => pickSixMinuteWalkRespiratoryRate(preData),
    [preData],
  );
  const respiratoryRatePost = useMemo(
    () => pickSixMinuteWalkRespiratoryRate(data),
    [data],
  );
  const heartRatePre = useMemo(() => pickHeartDxHrBpm(preData), [preData]);
  const heartRatePost = useMemo(() => pickHeartDxHrBpm(data), [data]);
  const respiratoryQualityPre = useMemo(
    () => pickSixMinuteWalkRespiratoryQuality(preData),
    [preData],
  );
  const respiratoryQualityPost = useMemo(
    () => pickSixMinuteWalkRespiratoryQuality(data),
    [data],
  );
  const heartQualityPre = useMemo(
    () => pickHrSignalQuality(preData),
    [preData],
  );
  const heartQualityPost = useMemo(() => pickHrSignalQuality(data), [data]);
  const distanceMeters = useMemo(
    () => pickSixMinuteWalkDistanceMeters(data),
    [data],
  );
  const totalTrackedDistanceMeters = useMemo(
    () => pickSixMinuteWalkTotalTrackedDistanceMeters(data),
    [data],
  );
  const recoveryDelayMs = useMemo(
    () => pickSixMinuteWalkRecoveryDelayMs(data),
    [data],
  );
  const geoRouteTrace = useMemo(() => pickSixMinuteWalkGeoRouteTrace(data), [data]);
  const routeTrace = useMemo(() => pickSixMinuteWalkRouteTrace(data), [data]);
  const inspiratoryReserve = useMemo(
    () => compareSixMinuteWalkInspiratoryReserve(preData, data),
    [preData, data],
  );
  const signalDatasets = useMemo(
    () => buildSignalDatasets({ preSignal, postSignal, t }),
    [postSignal, preSignal, t],
  );
  const recordingTime = useMemo(
    () =>
      formatRecordingIdDateParts(
        docId,
        i18n.resolvedLanguage || i18n.language || "en-GB",
      ),
    [docId, i18n.language, i18n.resolvedLanguage],
  );
  const preRecordingTimestamp = useMemo(
    () => parseTimestampFromRecordingId(pairedMeasurementId),
    [pairedMeasurementId],
  );
  const postRecordingTimestamp = useMemo(
    () => parseTimestampFromRecordingId(docId),
    [docId],
  );
  const rrUnit = t("result.resp.rrUnit", "bpm");
  const hrUnit = t("result.pulse.unit", "bpm");
  const distanceUnit = t("result.sixMinuteWalk.distanceUnit", "m");
  const secondsUnit = t("result.sixMinuteWalk.secondsUnit", "s");
  const summaryPreLabel = t("result.sixMinuteWalk.summaryPreLabel", "Pre");
  const summaryPostLabel = t("result.sixMinuteWalk.summaryPostLabel", "Post");
  const summaryChangeLabel = t(
    "result.sixMinuteWalk.summaryChangeLabel",
    "Change",
  );
  const summaryDistanceLabel = t(
    "result.sixMinuteWalk.summaryDistanceLabel",
    "Distance",
  );
  const summaryHeartRateLabel = t(
    "result.sixMinuteWalk.summaryHeartRateLabel",
    "Heart Rate",
  );
  const summarySixMinuteLabel = t(
    "result.sixMinuteWalk.summarySixMinuteLabel",
    "6 min",
  );
  const summaryTrackedLabel = t(
    "result.sixMinuteWalk.summaryTrackedLabel",
    "Tracked",
  );
  const summaryTimeToRecordingLabel = t(
    "result.sixMinuteWalk.summaryTimeToRecordingLabel",
    "Time to recording",
  );
  const summaryIrcLabel = t("result.sixMinuteWalk.summaryIrcLabel", "IRC");
  const maxSignalSecs = useMemo(
    () => Math.max(preSignal.maxSecs || 0, postSignal.maxSecs || 0, 60),
    [postSignal.maxSecs, preSignal.maxSecs],
  );
  const isBaselineOnly = stage === "pre";
  const pairedResultPending =
    !isBaselineOnly &&
    pairedMeasurementId &&
    !preMeasurement.loading &&
    !preMeasurement.error &&
    !preData;
  const preMeasurementUploadFailed = useMemo(
    () => Boolean(preMeasurement.exists && isUploadFailedDoc(preData)),
    [preData, preMeasurement.exists],
  );
  const preUploadFailUserMessage = useMemo(
    () => getUploadFailedUserMessage(preData),
    [preData],
  );
  const preMeasurementPending =
    !isBaselineOnly &&
    Boolean(pairedMeasurementId) &&
    !preMeasurementUploadFailed &&
    (preMeasurement.loading || pairedResultPending);
  const recordingQualitySummary = useMemo(() => {
    if (isBaselineOnly) {
      return summarizeRecordingQualityChecks([
        buildRecordingQualityCheck({
          key: "respiratory",
          label: t(
            "result.cardiopulmonary.respiratoryQuality",
            "Respiratory Quality",
          ),
          rawStatus: respiratoryQualityPost,
          recordingTimestamp: postRecordingTimestamp,
        }),
        buildRecordingQualityCheck({
          key: "cardiac",
          label: t("result.cardiopulmonary.cardiacQuality", "Cardiac Quality"),
          rawStatus: heartQualityPost,
          recordingTimestamp: postRecordingTimestamp,
        }),
      ]);
    }

    return summarizeRecordingQualityChecks([
      buildRecordingQualityCheck({
        key: "pre-respiratory",
        label: t(
          "result.sixMinuteWalk.preRespiratoryQuality",
          "Pre-walk respiratory quality",
        ),
        rawStatus: respiratoryQualityPre,
        recordingTimestamp: preMeasurementUploadFailed ? null : preRecordingTimestamp,
        forcePending: preMeasurementPending,
      }),
      buildRecordingQualityCheck({
        key: "post-respiratory",
        label: t(
          "result.sixMinuteWalk.postRespiratoryQuality",
          "Post-walk respiratory quality",
        ),
        rawStatus: respiratoryQualityPost,
        recordingTimestamp: postRecordingTimestamp,
      }),
      buildRecordingQualityCheck({
        key: "pre-cardiac",
        label: t(
          "result.sixMinuteWalk.preCardiacQuality",
          "Pre-walk cardiac quality",
        ),
        rawStatus: heartQualityPre,
        recordingTimestamp: preMeasurementUploadFailed ? null : preRecordingTimestamp,
        forcePending: preMeasurementPending,
      }),
      buildRecordingQualityCheck({
        key: "post-cardiac",
        label: t(
          "result.sixMinuteWalk.postCardiacQuality",
          "Post-walk cardiac quality",
        ),
        rawStatus: heartQualityPost,
        recordingTimestamp: postRecordingTimestamp,
      }),
    ]);
  }, [
    heartQualityPost,
    heartQualityPre,
    isBaselineOnly,
    postRecordingTimestamp,
    preMeasurementPending,
    preMeasurementUploadFailed,
    preRecordingTimestamp,
    respiratoryQualityPost,
    respiratoryQualityPre,
    t,
  ]);
  const respiratoryRatePreDisplay = buildPendingMetricDisplay(
    respiratoryRatePre,
    (value) => formatNumberValue(value),
    rrUnit,
    {
      recordingTimestamp: preMeasurementUploadFailed
        ? null
        : preRecordingTimestamp,
      forcePending: preMeasurementPending,
    },
  );
  const respiratoryRatePostDisplay = buildPendingMetricDisplay(
    respiratoryRatePost,
    (value) => formatNumberValue(value),
    rrUnit,
    { recordingTimestamp: postRecordingTimestamp },
  );
  const inspiratoryReservePreDisplay = buildPendingMetricDisplay(
    inspiratoryReserve.pre.peakHeightRatio,
    formatFoldValue,
    null,
    {
      recordingTimestamp: preMeasurementUploadFailed
        ? null
        : preRecordingTimestamp,
      forcePending: preMeasurementPending,
    },
  );
  const inspiratoryReservePostDisplay = buildPendingMetricDisplay(
    inspiratoryReserve.post.peakHeightRatio,
    formatFoldValue,
    null,
    { recordingTimestamp: postRecordingTimestamp },
  );
  const inspiratoryReserveChangeDisplay = buildPendingMetricDisplay(
    inspiratoryReserve.relativeChangePercent,
    formatPercentValue,
    null,
    {
      recordingTimestamp:
        preMeasurementUploadFailed || !postRecordingTimestamp
          ? null
          : postRecordingTimestamp,
      forcePending: preMeasurementPending,
    },
  );
  const heartRatePreDisplay = buildPendingMetricDisplay(
    heartRatePre,
    (value) => formatNumberValue(value),
    hrUnit,
    {
      recordingTimestamp: preMeasurementUploadFailed
        ? null
        : preRecordingTimestamp,
      forcePending: preMeasurementPending,
    },
  );
  const heartRatePostDisplay = buildPendingMetricDisplay(
    heartRatePost,
    (value) => formatNumberValue(value),
    hrUnit,
    { recordingTimestamp: postRecordingTimestamp },
  );
  const walkDistanceDisplay = buildPendingMetricDisplay(
    distanceMeters,
    formatMetersValue,
    distanceUnit,
    { recordingTimestamp: postRecordingTimestamp },
  );
  const trackedDistanceDisplay = buildPendingMetricDisplay(
    totalTrackedDistanceMeters,
    formatMetersValue,
    distanceUnit,
    { recordingTimestamp: postRecordingTimestamp },
  );
  const recoveryDelayDisplay = buildPendingMetricDisplay(
    recoveryDelayMs,
    formatSecondsFromMsValue,
    secondsUnit,
    { recordingTimestamp: postRecordingTimestamp },
  );
  const respiratorySummaryRows = [
    {
      leadingLabel: t("result.resp.rrShortLabel", "RR"),
      metrics: [
        {
          label: summaryPreLabel,
          value: respiratoryRatePreDisplay.value,
          unit: respiratoryRatePreDisplay.unit,
        },
        {
          label: summaryPostLabel,
          value: respiratoryRatePostDisplay.value,
          unit: respiratoryRatePostDisplay.unit,
        },
      ],
    },
    {
      leadingLabel: summaryIrcLabel,
      metrics: [
        {
          label: summaryPreLabel,
          value: inspiratoryReservePreDisplay.value,
          unit: inspiratoryReservePreDisplay.unit,
        },
        {
          label: summaryPostLabel,
          value: inspiratoryReservePostDisplay.value,
          unit: inspiratoryReservePostDisplay.unit,
        },
        {
          label: summaryChangeLabel,
          value: inspiratoryReserveChangeDisplay.value,
          unit: inspiratoryReserveChangeDisplay.unit,
        },
      ],
    },
  ];
  const heartRateSummaryRows = [
    {
      leadingLabel: summaryHeartRateLabel,
      metrics: [
        {
          label: summaryPreLabel,
          value: heartRatePreDisplay.value,
          unit: heartRatePreDisplay.unit,
        },
        {
          label: summaryPostLabel,
          value: heartRatePostDisplay.value,
          unit: heartRatePostDisplay.unit,
        },
      ],
    },
  ];
  const walkSummaryRows = [
    {
      leadingLabel: summaryDistanceLabel,
      metrics: [
        {
          label: summarySixMinuteLabel,
          value: walkDistanceDisplay.value,
          unit: walkDistanceDisplay.unit,
        },
        {
          label: summaryTrackedLabel,
          value: trackedDistanceDisplay.value,
          unit: trackedDistanceDisplay.unit,
        },
      ],
    },
  ];
  const compactRecordingQualityStatus = useMemo(() => {
    if (
      recordingQualitySummary.overallState !==
        RECORDING_QUALITY_OVERALL_STATES.PASSED &&
      recordingQualitySummary.overallState !==
        RECORDING_QUALITY_OVERALL_STATES.PENDING
    ) {
      return null;
    }

    const summaryCopy = getRecordingQualitySummaryCopy(t, recordingQualitySummary);
    return summaryCopy
      ? {
          tone: summaryCopy.tone,
          text: summaryCopy.title,
          accessibleLabel: summaryCopy.accessibleLabel,
        }
      : null;
  }, [recordingQualitySummary, t]);
  const showRecordingQualitySection = compactRecordingQualityStatus == null;

  useEffect(() => {
    if (!preSignal.points.length && !postSignal.points.length) {
      return;
    }

    console.log("[SixMinuteWalkInspiratoryReserve]", {
      docId,
      stage,
      pairedMeasurementId,
      inspiratoryReserve,
      preSignalPoints: preSignal.points,
      postSignalPoints: postSignal.points,
      preSignalPointCount: preSignal.points.length,
      postSignalPointCount: postSignal.points.length,
    });
  }, [
    docId,
    inspiratoryReserve,
    pairedMeasurementId,
    postSignal.points,
    preSignal.points,
    stage,
  ]);

  useEffect(() => {
    const routeSummaryKey = [
      docId || "no-doc-id",
      stage || "no-stage",
      routeTrace.length,
      geoRouteTrace.length,
      distanceMeters ?? "no-distance",
      totalTrackedDistanceMeters ?? "no-tracked-distance",
    ].join("|");

    if (lastRouteSummaryLogRef.current === routeSummaryKey) {
      return;
    }

    lastRouteSummaryLogRef.current = routeSummaryKey;

    logSixMinuteWalkResultDebug("info", "Resolved result route traces.", {
      docId,
      stage,
      pairedMeasurementId,
      routeTracePointCount: routeTrace.length,
      geoRoutePointCount: geoRouteTrace.length,
      distanceMeters,
      totalTrackedDistanceMeters,
      recoveryDelayMs,
      firstGeoPoint: geoRouteTrace[0] || null,
      lastGeoPoint: geoRouteTrace[geoRouteTrace.length - 1] || null,
      firstRoutePoint: routeTrace[0] || null,
      lastRoutePoint: routeTrace[routeTrace.length - 1] || null,
    });
  }, [
    distanceMeters,
    docId,
    geoRouteTrace,
    pairedMeasurementId,
    recoveryDelayMs,
    routeTrace,
    stage,
    totalTrackedDistanceMeters,
  ]);

  const details = (
    <section className="p-4 flex flex-col gap-4 w-full h-fit">
      <ResultCard
        icon={<LungIcon />}
        title={t("result.sixMinuteWalk.preSignalTitle", "Pre-walk signal")}
      >
        {preSignal.points.length ? (
          <SixMinuteWalkSignalChart
            datasets={[
              {
                key: "pre",
                label: t("result.sixMinuteWalk.preSignalTitle", "Pre-walk"),
                points: preSignal.points,
              },
            ]}
            maxSecs={preSignal.maxSecs || 60}
          />
        ) : (
          <div className="muted-text">
            {pairedMeasurementId && preMeasurement.loading
              ? t(
                  "result.sixMinuteWalk.loadingBaseline",
                  "Loading baseline recording…",
                )
              : t("result.resp.noSignal", "No signal trace available.")}
          </div>
        )}
      </ResultCard>

      <ResultCard
        icon={<LungIcon />}
        title={t("result.sixMinuteWalk.postSignalTitle", "Post-walk signal")}
      >
        {postSignal.points.length ? (
          <SixMinuteWalkSignalChart
            datasets={[
              {
                key: "post",
                label: t("result.sixMinuteWalk.postSignalTitle", "Post-walk"),
                points: postSignal.points,
              },
            ]}
            maxSecs={postSignal.maxSecs || 60}
          />
        ) : (
          <div className="muted-text">
            {t("result.resp.noSignal", "No signal trace available.")}
          </div>
        )}
      </ResultCard>

      <ResultCard
        icon={<StackedLineChartIcon />}
        title={t(
          "result.sixMinuteWalk.combinedSignalTitle",
          "Combined respiratory signal",
        )}
      >
        {signalDatasets.some((dataset) => dataset.points.length) ? (
          <SixMinuteWalkSignalChart
            datasets={signalDatasets}
            maxSecs={maxSignalSecs}
          />
        ) : (
          <div className="muted-text">
            {t("result.resp.noSignal", "No signal trace available.")}
          </div>
        )}
      </ResultCard>

      {geoRouteTrace.length || routeTrace.length ? (
        <ResultCard
          icon={<DirectionsWalkIcon />}
          title={t("result.sixMinuteWalk.routeCardTitle", "Walk route")}
        >
          <SixMinuteWalkRouteMap
            geoPoints={geoRouteTrace}
            fallbackPoints={routeTrace}
            ariaLabel={t(
              "result.sixMinuteWalk.routeChartAriaLabel",
              "Walk route trace",
            )}
          />
        </ResultCard>
      ) : null}
    </section>
  );

  return (
    <ResultViewScaffold
      title={getResultModeLabel(variant, t)}
      recordingTime={recordingTime}
      icon={getResultModeIcon(variant)}
      statusLine={compactRecordingQualityStatus}
      details={details}
      footer={footer}
    >
      {isBaselineOnly ? (
        <ResultCard>
          <p className="text-sm text-typography">
            {t(
              "result.sixMinuteWalk.baselineOnlyMessage",
              "This is the baseline recording. Complete the walk and post-walk recording to see the full 6-Minute Walk result.",
            )}
          </p>
        </ResultCard>
      ) : null}

      {pairedResultPending ? (
        <ResultCard>
          <p className="text-sm text-typography">
            {t(
              "result.sixMinuteWalk.pairedPendingMessage",
              "We found the post-walk recording, but the paired baseline result is still loading.",
            )}
          </p>
        </ResultCard>
      ) : null}

      {preMeasurementUploadFailed ? (
        <ResultCard>
          <div className="flex flex-col gap-2 text-left">
            <p className="text-sm font-semibold text-typography">
              {t(
                "result.sixMinuteWalk.pairedUploadFailedTitle",
                "Baseline recording upload failed",
              )}
            </p>
            <p className="text-sm text-typography">
              {t(
                preUploadFailUserMessage?.bodyKey ||
                  "result.sixMinuteWalk.pairedUploadFailedBody",
                preUploadFailUserMessage?.bodyDefault ||
                  "We couldn't finish uploading the baseline recording for this walk assessment.",
              )}
            </p>
            <p className="text-sm text-typography">
              {t(
                preUploadFailUserMessage?.hintKey ||
                  "result.sixMinuteWalk.pairedUploadFailedHint",
                preUploadFailUserMessage?.hintDefault ||
                  "Keep the app open until uploads finish before leaving Wi-Fi or switching apps, then repeat the baseline recording.",
              )}
            </p>
          </div>
        </ResultCard>
      ) : null}

      {showRecordingQualitySection ? (
        <RecordingQualitySection
          summary={recordingQualitySummary}
          guidanceLinkTo="/guidance/cardiopulmonary"
          guidanceCtaLabel={t("result.cardiopulmonary.guidanceCta", "Tips")}
          repeatLinkTo={repeatLinkTo}
          repeatCtaLabel={t("result.recordingQuality.repeatCta", "Repeat")}
        />
      ) : null}

      <MultiMetricSummarySection
        icon={<DirectionsWalkIcon />}
        title={t("result.sixMinuteWalk.walkSummaryTitle", "Walk summary")}
        rows={walkSummaryRows}
      >
        <ResultSummaryRow
          label={summaryTimeToRecordingLabel}
          value={recoveryDelayDisplay.value}
          unit={recoveryDelayDisplay.unit}
        />
      </MultiMetricSummarySection>

      <MultiMetricSummarySection
        icon={<LungIcon />}
        title={t("result.sixMinuteWalk.respiratoryTitle", "Respiratory")}
        rows={respiratorySummaryRows}
      />

      <MultiMetricSummarySection
        icon={<ECGHeart />}
        title={t("result.sixMinuteWalk.heartRateTitle", "Heart Rate")}
        rows={heartRateSummaryRows}
      />
    </ResultViewScaffold>
  );
}

const SixMinuteWalkResultView = memo(SixMinuteWalkResultViewImpl);

SixMinuteWalkResultView.isReady = (data) => pickRr(data) != null;

export default SixMinuteWalkResultView;
